import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { asLudusLeadId } from '@ludus/core'
import { Badge, cn } from '@ludus/ui'
import {
  fetchSale,
  type Sale,
  type SaleApiError,
  type SaleCard,
  type SalePipelinePresence,
} from '@/lib/api/ludus-leads.ts'
import { Breadcrumb } from '@/shared/breadcrumb/Breadcrumb.tsx'
import { CenteredPanel } from '@/shared/feedback/CenteredPanel.tsx'

/**
 * One sale, every card — read-only in v1 (ruled 2026-08-27, §9 Q18).
 *
 * **This is the only page allowed to say two cards are the same sale.** It
 * reads `GET /api/ludus-leads/:id`, which answers the whole cycle in one
 * request: the view built out of ids, plus the card rows and board rows those
 * ids name. Fetching a card per row would be the N+1 that composition exists to
 * avoid, and joining pipelines any other way would be a second, competing
 * notion of identity (root CLAUDE.md, pillar 5).
 *
 * **Coverage is reported, not scolded.** A deal with no calls, a cycle in one
 * pipeline only — those are ordinary states of a real sales process, so the
 * strip counts them rather than flagging them. What *is* flagged is a `low`
 * link: written, visible, and deliberately not counted, because a doubt is
 * queued for a human rather than resolved by a coin flip.
 *
 * Read-only means read-only: there is no stage move, no assign, no spawn here.
 * A card's own page is where it is edited, and every row links to it.
 */
export function SalePage() {
  const { t } = useTranslation()
  const params = useParams<{ ludusLeadId: string }>()
  const saleId = params.ludusLeadId ? asLudusLeadId(params.ludusLeadId) : null

  const swr = useSWR<Sale, SaleApiError>(saleId ? `/ludus-leads/${saleId}` : null, () =>
    fetchSale(saleId as ReturnType<typeof asLudusLeadId>),
  )

  if (!saleId) {
    return (
      <CenteredPanel
        title={t('pages.sale.invalid.title', { defaultValue: 'Invalid URL' })}
        body={t('pages.sale.invalid.body', { defaultValue: 'The URL is missing a sale id.' })}
      />
    )
  }

  if (swr.isLoading) {
    return (
      <CenteredPanel title={t('pages.sale.loading', { defaultValue: 'Loading sale…' })} muted />
    )
  }

  if (swr.error) {
    const error = swr.error
    return (
      <CenteredPanel
        title={t('pages.sale.error.title', { defaultValue: 'Could not load this sale' })}
        body={
          error.kind === 'not_found'
            ? t('pages.sale.error.notFound', {
                defaultValue: 'This sale is no longer available.',
              })
            : t('pages.sale.error.generic', {
                defaultValue: 'The engine returned an error while loading this sale.',
              })
        }
        meta={error.status !== null ? `HTTP ${error.status} · ${error.message}` : undefined}
        tone="error"
      />
    )
  }

  if (!swr.data) return null
  const sale = swr.data

  return (
    <div className="flex w-full flex-1 flex-col gap-4 p-4 sm:p-6">
      <Breadcrumb
        items={[
          {
            label: t('pages.sale.breadcrumb.pipelines', { defaultValue: 'Pipelines' }),
            to: '/pipelines',
          },
          { label: t('pages.sale.breadcrumb.sale', { defaultValue: 'Sale' }) },
        ]}
      />

      <SaleHeader sale={sale} />
      <CoverageStrip sale={sale} />

      <div className="flex flex-col gap-3">
        {sale.pipelines.map((presence) => (
          <PipelineGroup key={presence.pipelineId} sale={sale} presence={presence} />
        ))}
      </div>

      <Timeline sale={sale} />
    </div>
  )
}

function SaleHeader({ sale }: { sale: Sale }) {
  const { t, i18n } = useTranslation()
  return (
    <header className="flex flex-col gap-2 rounded-xl border border-border bg-background-secondary p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold text-foreground">
          {t('pages.sale.title', { defaultValue: 'Sale' })}
        </h1>
        <Badge tone={sale.closedAt === null ? 'primary' : 'muted'} size="sm">
          {sale.closedAt === null
            ? t('pages.sale.open', { defaultValue: 'Open' })
            : t('pages.sale.closed', { defaultValue: 'Closed' })}
        </Badge>
        {/* Anomaly flags are the layer's own doubts, surfaced rather than
            swallowed — a flag nobody can see is a flag nobody acts on. */}
        {sale.anomalyFlags.map((flag) => (
          <Badge key={flag} tone="warning" size="sm">
            {t(`pages.sale.anomaly.${flag}`, { defaultValue: flag })}
          </Badge>
        ))}
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <Field label={t('pages.sale.opened', { defaultValue: 'Opened' })}>
          {sale.openedAt.toLocaleString(i18n.language)}
        </Field>
        {sale.closedAt !== null && (
          <Field label={t('pages.sale.closedAt', { defaultValue: 'Closed' })}>
            {sale.closedAt.toLocaleString(i18n.language)}
          </Field>
        )}
        <Field label={t('pages.sale.origin', { defaultValue: 'Origin' })}>
          {t(`pages.sale.originKind.${sale.origin}`, { defaultValue: sale.origin })}
        </Field>
        {sale.productKey !== null && (
          <Field label={t('pages.sale.product', { defaultValue: 'Product' })}>
            {sale.productKey}
          </Field>
        )}
      </dl>

      {/* The customer is a set of identifiers and nothing else — no name, no
          fields, no behaviour (root CLAUDE.md, "customer"). Rendering more
          would be inventing a profile this layer deliberately does not keep. */}
      {sale.identifiers.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {sale.identifiers.map((id) => (
            <Badge key={`${id.kind}:${id.value}`} tone="muted" size="sm">
              {t(`pages.sale.identifier.${id.kind}`, { defaultValue: id.kind })}: {id.value}
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-[11px] italic text-muted">
          {t('pages.sale.noIdentifiers', {
            defaultValue: 'No identifiers recorded for this customer.',
          })}
        </p>
      )}
    </header>
  )
}

function CoverageStrip({ sale }: { sale: Sale }) {
  const { t } = useTranslation()
  const c = sale.coverage
  return (
    <section className="flex flex-wrap gap-x-6 gap-y-2 rounded-xl border border-border bg-background-secondary px-4 py-3">
      <Stat label={t('pages.sale.coverage.cards', { defaultValue: 'Cards' })} value={c.cards} />
      <Stat label={t('pages.sale.coverage.deals', { defaultValue: 'Deals' })} value={c.dealCards} />
      <Stat
        label={t('pages.sale.coverage.interactions', { defaultValue: 'Interactions' })}
        value={c.interactionCards}
      />
      <Stat
        label={t('pages.sale.coverage.pipelines', { defaultValue: 'Pipelines' })}
        value={c.pipelines}
      />
      {/* Called out rather than blended in: a disputed link is present, visible
          and not counted, which is the whole point of `low` confidence. */}
      <Stat
        label={t('pages.sale.coverage.disputed', { defaultValue: 'Disputed links' })}
        value={c.disputedLinks}
        tone={c.disputedLinks > 0 ? 'warning' : undefined}
      />
    </section>
  )
}

function PipelineGroup({ sale, presence }: { sale: Sale; presence: SalePipelinePresence }) {
  const { t } = useTranslation()
  const board = sale.boards[presence.pipelineId]

  return (
    <section className="flex flex-col rounded-xl border border-border bg-background-secondary">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <span className="truncate text-sm font-semibold text-foreground">
          {board?.name ?? presence.pipelineId}
        </span>
        <Badge tone="muted" size="sm">
          {t(`pages.sale.kind.${presence.pipelineKind}`, { defaultValue: presence.pipelineKind })}
        </Badge>
        {board?.hasAdapter && (
          <Badge tone="info" size="sm">
            {t('pages.sale.mirrored', { defaultValue: 'Mirrored' })}
          </Badge>
        )}
        {presence.disputed > 0 && (
          <Badge tone="warning" size="sm">
            {t('pages.sale.disputedN', {
              count: presence.disputed,
              defaultValue: '{{count}} disputed',
            })}
          </Badge>
        )}
      </header>

      <ul className="flex flex-col">
        {presence.cards.map((card) => (
          <CardRow key={card.id} sale={sale} card={card} />
        ))}
      </ul>
    </section>
  )
}

function CardRow({ sale, card }: { sale: Sale; card: SaleCard }) {
  const { t, i18n } = useTranslation()
  const lead = sale.leads[card.id]

  return (
    <li>
      <Link
        to={`/pipelines/${card.pipelineId}/leads/${card.id}`}
        className="flex flex-wrap items-center gap-2 border-b border-border/50 px-4 py-2 text-left transition-colors last:border-b-0 hover:bg-sidebar-hover-bg"
      >
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">
          {lead?.name ?? card.id}
        </span>

        {card.link.confidence === 'low' && (
          <Badge tone="warning" size="sm">
            {t('pages.sale.link.low', { defaultValue: 'Disputed' })}
          </Badge>
        )}

        <span className="shrink-0 text-[11px] text-muted">
          {t(`pages.sale.link.method.${card.link.method}`, { defaultValue: card.link.method })}
        </span>
        <span className="shrink-0 text-[10px] text-muted">
          {card.occurredAt.toLocaleDateString(i18n.language)}
        </span>
      </Link>
    </li>
  )
}

function Timeline({ sale }: { sale: Sale }) {
  const { t, i18n } = useTranslation()
  if (sale.cards.length === 0) return null

  return (
    <section className="flex flex-col rounded-xl border border-border bg-background-secondary">
      <header className="border-b border-border px-4 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
          {t('pages.sale.timeline', { defaultValue: 'Timeline' })}
        </span>
      </header>
      <ol className="flex flex-col gap-1 px-4 py-3">
        {/* One timeline across every board — the question the layer exists to
            answer, and the one a single pipeline's card list cannot. */}
        {sale.cards.map((card) => (
          <li key={card.id} className="flex min-w-0 items-baseline gap-2 text-xs">
            <span className="shrink-0 font-mono text-[10px] text-muted">
              {card.occurredAt.toLocaleString(i18n.language)}
            </span>
            <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted">
              {sale.boards[card.pipelineId]?.name ?? card.pipelineId}
            </span>
            <span className="min-w-0 flex-1 truncate text-foreground">
              {sale.leads[card.id]?.name ?? card.id}
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-[10px] uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-foreground">{children}</dd>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'warning' }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted">{label}</span>
      <span
        className={cn(
          'text-lg font-semibold tabular-nums',
          tone === 'warning' ? 'text-toast-warning-text' : 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  )
}
